import { assert } from '@chainflip/utils/assertion';
import {
  type InternalAssetMap,
  type AnyChainflipAsset,
  type AnyInternalAssetMap,
  type ChainflipAsset,
  type AnyChainMap,
  type ChainMap,
} from '@chainflip/utils/chainflip';
import {
  backspinEthereum,
  bitcoin,
  bitcoinTestnet,
  type ChainData,
  dotTestnet,
  ethereum,
  polkadot,
  sepolia,
  arbitrum,
  backspinArbitrum,
  arbitrumSepolia,
  backspinSolana,
  solana,
  solanaDevnet,
  assethub,
  hubTestnet,
  type AnyChainData,
  type AnyChainId,
  tronTestnet,
  tron,
  backspinTron,
  bsc,
  bscTestnet,
  backspinBsc,
} from '@/shared/assets/chains';
import {
  beth$,
  bflip$,
  btc$,
  busdc$,
  busdt$,
  type ChainflipToken,
  dot$,
  eth$,
  flip$,
  pdot$,
  tbtc$,
  usdc$,
  sflip$,
  susdc$,
  seth$,
  usdt$,
  susdt$,
  arbusdc$,
  arbeth$,
  barbeth$,
  barbusdc$,
  sarbeth$,
  sarbusdc$,
  sol$,
  solusdc$,
  tsol$,
  bsol$,
  bsolusdc$,
  tsolusdc$,
  hubdot$,
  hubusdt$,
  hubusdc$,
  hubpusdt$,
  hubpusdc$,
  hubpdot$,
  solusdt$,
  arbusdt$,
  wbtc$,
  bwbtc$,
  barbusdt$,
  bsolusdt$,
  swbtc$,
  sarbusdt$,
  tsolusdt$,
  type AnyChainflipToken,
  trx$,
  trxusdt$,
  btrx$,
  btrxusdt$,
  ntrxusdt$,
  ntrx$,
  cbbtc$,
  scbbtc$,
  bcbbtc$,
  bnb$,
  tbnb$,
  bbnb$,
  bscusdt$,
  tbscusdt$,
  bbscusdt$,
} from '@/shared/assets/tokens';

const chainflipNetworks = ['unknown', 'backspin', 'sisyphos', 'perseverance', 'mainnet'] as const;

export type ChainflipNetwork = (typeof chainflipNetworks)[number];

function assertChainflipNetwork(network: string): asserts network is ChainflipNetwork {
  assert(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chainflipNetworks.includes(network as any),
    `Invalid network: "${network}"`,
  );
}

export const getChainflipNetwork = (): ChainflipNetwork => {
  // using unknown because this function is bundled in the shared package
  const network = process.env.NEXT_PUBLIC_CHAINFLIP_NETWORK ?? 'unknown';
  assertChainflipNetwork(network);
  return network;
};

export const getKnownChainflipNetwork = (): Exclude<ChainflipNetwork, 'unknown'> => {
  const network = getChainflipNetwork();
  assert(network !== 'unknown', 'Network cannot be unknown');
  return network;
};

export const getEthereumChain = () => {
  const network = getChainflipNetwork();
  if (network === 'backspin') return backspinEthereum;
  if (network === 'mainnet') return ethereum;
  return sepolia;
};

const getArbitrumChain = () => {
  const network = getChainflipNetwork();
  if (network === 'backspin') return backspinArbitrum;
  if (network === 'mainnet') return arbitrum;
  return arbitrumSepolia;
};

const getSolanaChain = () => {
  const network = getChainflipNetwork();
  if (network === 'backspin') return backspinSolana;
  if (network === 'mainnet') return solana;
  return solanaDevnet;
};

const getTronChain = () => {
  const network = getChainflipNetwork();
  if (network === 'backspin') return backspinTron;
  if (network === 'mainnet') return tron;
  return tronTestnet;
};

const getBscChain = () => {
  const network = getChainflipNetwork();
  if (network === 'backspin') return backspinBsc;
  if (network === 'mainnet') return bsc;
  return bscTestnet;
};

export const getChainflipToken = (asset: ChainflipAsset): ChainflipToken => {
  const network = getChainflipNetwork();

  if (network === 'mainnet') {
    return (
      {
        Flip: flip$,
        Usdc: usdc$,
        Usdt: usdt$,
        Wbtc: wbtc$,
        Eth: eth$,
        Btc: btc$,
        ArbUsdc: arbusdc$,
        ArbUsdt: arbusdt$,
        ArbEth: arbeth$,
        Sol: sol$,
        SolUsdc: solusdc$,
        SolUsdt: solusdt$,
        HubDot: hubdot$,
        HubUsdt: hubusdt$,
        HubUsdc: hubusdc$,
        Trx: trx$,
        TrxUsdt: trxusdt$,
        Cbbtc: cbbtc$,
        Bnb: bnb$,
        BscUsdt: bscusdt$,
      } satisfies InternalAssetMap<ChainflipToken>
    )[asset];
  }

  if (network === 'backspin') {
    return (
      {
        Flip: bflip$,
        Usdc: busdc$,
        Usdt: busdt$,
        Wbtc: bwbtc$,
        Eth: beth$,
        Btc: tbtc$,
        ArbUsdc: barbusdc$,
        ArbUsdt: barbusdt$,
        ArbEth: barbeth$,
        Sol: bsol$,
        SolUsdc: bsolusdc$,
        SolUsdt: bsolusdt$,
        HubDot: hubpdot$,
        HubUsdt: hubpusdt$,
        HubUsdc: hubpusdc$,
        Trx: btrx$,
        TrxUsdt: btrxusdt$,
        Cbbtc: bcbbtc$,
        Bnb: bbnb$,
        BscUsdt: bbscusdt$,
      } satisfies InternalAssetMap<ChainflipToken>
    )[asset];
  }

  return (
    {
      Flip: sflip$,
      Usdc: susdc$,
      Usdt: susdt$,
      Eth: seth$,
      Wbtc: swbtc$,
      Btc: tbtc$,
      ArbUsdc: sarbusdc$,
      ArbUsdt: sarbusdt$,
      ArbEth: sarbeth$,
      Sol: tsol$,
      SolUsdc: tsolusdc$,
      SolUsdt: tsolusdt$,
      HubDot: hubpdot$,
      HubUsdt: hubpusdt$,
      HubUsdc: hubpusdc$,
      Trx: ntrx$,
      TrxUsdt: ntrxusdt$,
      Cbbtc: scbbtc$,
      Bnb: tbnb$,
      BscUsdt: tbscusdt$,
    } satisfies InternalAssetMap<ChainflipToken>
  )[asset];
};

export const getAnyChainflipToken = (asset: AnyChainflipAsset): AnyChainflipToken => {
  switch (asset) {
    case 'Dot':
      if (getChainflipNetwork() === 'mainnet') return dot$;
      return pdot$;
    default:
      return getChainflipToken(asset);
  }
};

export const chainflipChainMap = {
  Bitcoin: getChainflipNetwork() === 'mainnet' ? bitcoin : bitcoinTestnet,
  Ethereum: getEthereumChain(),
  Arbitrum: getArbitrumChain(),
  Solana: getSolanaChain(),
  Assethub: getChainflipNetwork() === 'mainnet' ? assethub : hubTestnet,
  Tron: getTronChain(),
  Bsc: getBscChain(),
} as const satisfies ChainMap<ChainData>;

export const anyChainflipChainMap = {
  ...chainflipChainMap,
  Polkadot: getChainflipNetwork() === 'mainnet' ? polkadot : dotTestnet,
} as const satisfies AnyChainMap<AnyChainData>;

export const chainflipAssetMap = {
  Eth: getChainflipToken('Eth'),
  Usdc: getChainflipToken('Usdc'),
  Usdt: getChainflipToken('Usdt'),
  Wbtc: getChainflipToken('Wbtc'),
  Flip: getChainflipToken('Flip'),
  Btc: getChainflipToken('Btc'),
  ArbUsdc: getChainflipToken('ArbUsdc'),
  ArbUsdt: getChainflipToken('ArbUsdt'),
  ArbEth: getChainflipToken('ArbEth'),
  Sol: getChainflipToken('Sol'),
  SolUsdc: getChainflipToken('SolUsdc'),
  SolUsdt: getChainflipToken('SolUsdt'),
  HubDot: getChainflipToken('HubDot'),
  HubUsdt: getChainflipToken('HubUsdt'),
  HubUsdc: getChainflipToken('HubUsdc'),
  Trx: getChainflipToken('Trx'),
  TrxUsdt: getChainflipToken('TrxUsdt'),
  Cbbtc: getChainflipToken('Cbbtc'),
  Bnb: getChainflipToken('Bnb'),
  BscUsdt: getChainflipToken('BscUsdt'),
} as const satisfies InternalAssetMap<ChainflipToken>;

export const anyChainflipAssetMap = {
  ...chainflipAssetMap,
  Dot: getAnyChainflipToken('Dot'),
} as const satisfies AnyInternalAssetMap<AnyChainflipToken>;

export const getChainflipAsset = (
  chainId: AnyChainId,
  tokenAddress: string,
): AnyChainflipAsset | undefined =>
  Object.entries(chainflipAssetMap).find(
    ([, token]) => token.chain.id === chainId && token.address === tokenAddress,
  )?.[0] as AnyChainflipAsset | undefined;

export const FLIP_SYMBOL = chainflipAssetMap.Flip.symbol;

export const STAKING_URL =
  process.env.NEXT_PUBLIC_STAKING_URL ??
  (getChainflipNetwork() === 'mainnet'
    ? 'https://auctions.chainflip.io'
    : 'https://auctions.perseverance.chainflip.io');

export const SWAP_URL =
  process.env.NEXT_PUBLIC_SWAPPING_URL ??
  (getChainflipNetwork() === 'mainnet'
    ? 'https://swap.chainflip.io'
    : 'https://swap.perseverance.chainflip.io');
